/**
 * @bos/portal3 — the register page (route `/register`, inside <AuthLayout>, wrapped in
 * <RedirectIfAuthed>).
 *
 * Wired to `auth.register`: a valid submission creates the account, opens a session
 * (cookie set server-side), and lands the user on the dashboard already signed in. A
 * duplicate email or a password that fails the policy surfaces the server's clean
 * message inline.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PATHS } from '../routes';
import { useAuth, authErrorMessage } from '../auth/AuthContext';

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ email, password, displayName });
      navigate(PATHS.dashboard, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
      setBusy(false);
    }
  }

  return (
    <section className="authcard">
      <h1 className="authcard__title">Create your account</h1>
      <p className="authcard__lede">Set up a private, calm home for your business.</p>
      <form className="form" onSubmit={onSubmit} aria-label="Create account">
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        <label className="form__field">
          <span className="form__label">Your name</span>
          <input
            className="form__input"
            type="text"
            name="displayName"
            autoComplete="name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="form__field">
          <span className="form__label">Email</span>
          <input
            className="form__input"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="form__field">
          <span className="form__label">Password</span>
          <input
            className="form__input"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          <p className="form__hint">At least 8 characters.</p>
        </label>
        <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </button>
      </form>
      <p className="authcard__switch">
        Already have an account? <Link to={PATHS.login}>Sign in</Link>
      </p>
    </section>
  );
}
